import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import {
  contextoEpp,
  contextoMve,
  diferencaLinha,
  errosDoLancamentoMve,
  errosEpp,
  formularioMedicao,
  horimetroInicial,
  iniciosDasLinhas,
  modoDia,
  papelDaLinha,
  previaValorEpp,
  rotuloMve,
  useParteDiariaStore,
} from '@/features/parteDiaria/parteDiariaStore'
import { saveParteDiaria } from '@/features/parteDiaria/parteDiariaRepo'
import { deleteDraft } from '@/features/parteDiaria/draftParteDiariaRepo'
import { useContextoParteDiaria } from '@/features/parteDiaria/useParteDiaria'
import { dataBR } from '@/features/parteDiaria/parteDiariaRegras'
import { Alerta } from '@/features/parteDiaria/fields'
import { fmtBRL, fmtNum } from '@/features/parteDiaria/format'

/**
 * Passo 2: revisão. Online, recarrega o contexto (o dia pode ter sido lançado
 * no SIGA web ou o horímetro pode ter mudado) e só então salva no outbox.
 */
export function ParteDiariaStep2Route() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()
  const store = useParteDiariaStore()
  const ctx = useContextoParteDiaria(store.mveId, store.dt)
  const [saving, setSaving] = useState(false)
  // Depois de salvar, o store é zerado ANTES de a navegação para /success
  // comitar (zustand re-renderiza síncrono; o navigate do react-router entra
  // como transição). Sem esta trava, o guard abaixo via o store vazio e mandava
  // o operador de volta ao início — parecia que o lançamento não tinha salvo.
  const salvouRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const horimetroAntes = horimetroInicial(store)
  const modoAntes = modoDia(store)
  useEffect(() => {
    if (ctx.status === 'live' && ctx.contexto) store.setContexto(ctx.contexto, 'live')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, ctx.contexto])

  if (!authed) return <Navigate to="/login" replace />
  if (salvouRef.current) return null
  if (store.mveId == null || store.modulo == null) return <Navigate to="/parte-diaria/step0" replace />

  const cMve = contextoMve(store)
  const cEpp = contextoEpp(store)
  const errosMve = store.modulo === 'MVE' ? errosDoLancamentoMve(store) : []
  const errosPeriodo = store.modulo === 'EPP' ? errosEpp(store) : []
  const bloqueios = store.contexto?.bloqueios ?? []
  const diaBloqueado = cMve?.dia.modo === 'BLOQUEADO' || (cEpp != null && !cEpp.podeInserir)
  const mudouHorimetro = ctx.status === 'live' && horimetroAntes != null && horimetroInicial(store) !== horimetroAntes
  const mudouModo = ctx.status === 'live' && modoAntes !== modoDia(store)
  const temErro = errosMve.length > 0 || errosPeriodo.length > 0 || bloqueios.length > 0 || diaBloqueado
  const formulario = formularioMedicao(store)
  const inicios = iniciosDasLinhas(store)
  const previa = previaValorEpp(store)

  async function handleSave() {
    if (temErro || ctx.status === 'loading') return
    setSaving(true)
    setError(null)
    try {
      const { pushResult } = await saveParteDiaria(store)
      const resumo = store.modulo === 'MVE' ? `${store.linhas.length} linha(s)` : 'período'
      await deleteDraft(store.id)
      salvouRef.current = true
      navigate('/parte-diaria/success', { replace: true, state: { resumo, pushResult } })
      store.reset()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/parte-diaria/step1')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold text-on-surface">Parte Diária · 3 de 3 · Revisar</h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-2xl mx-auto w-full">
        {ctx.status === 'loading' && (
          <p className="text-xs text-on-surface-dim inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Conferindo regras do SIGA…
          </p>
        )}
        {ctx.message && <p className="text-xs text-warn">{ctx.message}</p>}
        {bloqueios.length > 0 && (
          <Alerta tone="bad">
            <span className="block font-medium">O SIGA não aceita este lançamento</span>
            {bloqueios.map((b) => (
              <span key={b.code} className="block">
                {b.message}
              </span>
            ))}
          </Alerta>
        )}
        {diaBloqueado && <Alerta tone="bad">{cMve?.dia.message ?? cEpp?.message}</Alerta>}
        {(mudouHorimetro || mudouModo) && (
          <Alerta tone="warn">
            {mudouModo ? 'O dia mudou no SIGA desde o passo anterior (já há linhas nesta data). ' : ''}
            {mudouHorimetro ? `O horímetro inicial mudou para ${fmtNum(horimetroInicial(store), 0)}. ` : ''}
            Confira as linhas antes de salvar.
          </Alerta>
        )}
        {errosMve.length > 0 && (
          <Alerta tone="bad">
            <span className="block font-medium">Corrija antes de salvar</span>
            {errosMve.flatMap((f) =>
              f.erros.map((e) => (
                <span key={`${f.linhaId}-${e.campo}-${e.mensagem}`} className="block">
                  Linha {f.indice + 1}: {e.mensagem}
                </span>
              )),
            )}
          </Alerta>
        )}
        {errosPeriodo.length > 0 && (
          <Alerta tone="bad">
            {errosPeriodo.map((e) => (
              <span key={`${e.campo}-${e.mensagem}`} className="block">
                {e.mensagem}
              </span>
            ))}
          </Alerta>
        )}
        {(store.contexto?.avisos.length ?? 0) > 0 && (
          <Alerta tone="warn">
            {store.contexto!.avisos.map((a) => (
              <span key={a} className="block">
                {a}
              </span>
            ))}
          </Alerta>
        )}

        <div className="card-surface p-4 space-y-2">
          <h2 className="text-xs font-bold text-accent uppercase">Equipamento</h2>
          <Row label="Prefixo" value={rotuloMve(store)} />
          <Row label="Tipo" value={store.mveTipo === 'E' ? 'EPP (período)' : `MVE ${store.mveTipo ?? ''}`} />
          <Row label="Data" value={dataBR(store.dt)} />
          {store.modulo === 'MVE' && <Row label="Modo" value={modoDia(store) === 'DIA_NOVO' ? 'Dia novo (1ª linha principal)' : 'Frações de um dia já lançado'} />}
          <Row label="Chefe da linha" value="definido pelo SIGA (projeto da obra na data)" />
        </div>

        {store.modulo === 'MVE' ? (
          <div>
            <h2 className="text-xs font-bold text-accent uppercase px-1 mb-2">Linhas ({store.linhas.length})</h2>
            <ul className="space-y-2">
              {store.linhas.map((l, i) => (
                <li key={l.id} className="card-surface-variant px-4 py-3">
                  <p className="text-sm text-on-surface font-medium truncate">
                    {papelDaLinha(store, i) === 'PRINCIPAL' ? 'Principal' : `Fração ${i + 1}`} · {l.servico?.nome ?? '—'}
                  </p>
                  <p className="text-xs text-on-surface-dim mt-0.5 truncate">
                    {l.obraNome}
                    {l.equipeNome ? ` · ${l.equipeNome}` : ''}
                    {l.chapa ? ` · chapa ${l.chapa}` : ''}
                  </p>
                  <p className="text-xs text-on-surface mt-1 font-mono tabular">
                    {formulario === 'P' ? '' : `${inicios[i] != null ? fmtNum(inicios[i], 0) : '?'} → `}
                    {fmtNum(l.medicaoTermino, formulario === 'P' ? 2 : 0)}
                    {diferencaLinha(store, i) != null ? ` (dif ${fmtNum(diferencaLinha(store, i), formulario === 'P' ? 2 : 0)})` : ''}
                    {' · '}
                    {l.hInicio}–{l.hFim}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="card-surface p-4 space-y-2">
            <h2 className="text-xs font-bold text-accent uppercase">Período</h2>
            <Row label="Obra" value={store.epp.obraNome} />
            <Row label="Serviço" value={store.epp.servico?.nome ?? '—'} />
            <Row label="De" value={store.epp.dtInicial ? dataBR(store.epp.dtInicial) : '—'} />
            <Row label="Até" value={store.epp.dtFinal ? dataBR(store.epp.dtFinal) : '—'} />
            <Row label="Valor estimado" value={previa ? fmtBRL(previa.valor) : '—'} />
            {store.epp.observacao && <Row label="Observação" value={store.epp.observacao} />}
          </div>
        )}

        {error && <p className="text-sm text-bad text-center">{error}</p>}
      </div>

      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button variant="primary" size="lg" disabled={saving || temErro || ctx.status === 'loading'} onClick={handleSave} className="w-full">
          {saving ? 'Salvando…' : store.modulo === 'MVE' ? `Salvar dia (${store.linhas.length} linha(s))` : 'Salvar período'}
        </Button>
      </div>
    </main>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-on-surface-dim">{label}</span>
      <span className="text-on-surface text-right">{value || '—'}</span>
    </div>
  )
}
